// One hook per admin domain. Each fetches from the signal server and returns
// ONLY real data. There is deliberately no fallback data: if the fetch fails,
// the hook returns an empty list and the error, and the page shows an honest
// error state naming what failed. Fabricated rows used to mask an unreachable or
// re-authenticating server, which read as "my real users vanished" the moment
// real data loaded over the fakes. Never again.

import { useEffect, useState } from 'react'
import {
  listUsers,
  listTickets,
  listMetrics,
  listMarketplace,
  type RemoteAdminUser,
  type RemoteTicket,
  type RemoteDailyMetric,
  type RemoteMarketplaceItem,
  type ActivityBasis
} from './adminClient'
import type {
  AdminUser,
  AdminTicket,
  DailyMetric,
  MarketplaceItem
} from './domain'

interface RemoteState<T> {
  data: T[]
  loading: boolean
  /** The server's (or the network's) error when the read failed, else null.
   *  Pages MUST show it: with `data` empty, a page that ignores `error` would
   *  render an unreachable server as "no users" or "$0", which reads as real. */
  error: string | null
  /** Re-fetch from the server. Used by pages after a mutation so the UI
   *  reflects the persisted state. */
  refresh: () => Promise<void>
}

function useRemoteList<TRemote, TLocal>(
  fetcher: () => Promise<
    | { ok: true; data: { ok: true } & Record<string, unknown> }
    | { ok: false; error: string; status: number }
  >,
  extract: (body: Record<string, unknown>) => TRemote[],
  adapt: (r: TRemote) => TLocal
): RemoteState<TLocal> {
  const [state, setState] = useState<Omit<RemoteState<TLocal>, 'refresh'>>({
    data: [],
    loading: true,
    error: null
  })
  const apply = (
    res: { ok: true; data: { ok: true } & Record<string, unknown> } | { ok: false; error: string; status: number }
  ): void => {
    if (res.ok) {
      const items = extract(res.data as unknown as Record<string, unknown>).map(adapt)
      setState({ data: items, loading: false, error: null })
    } else {
      // On failure show nothing real-looking: an empty list plus the error,
      // which the page renders. Never fabricate rows.
      setState({ data: [], loading: false, error: res.error })
    }
  }
  const load = async (): Promise<void> => {
    apply(await fetcher())
  }
  useEffect(() => {
    let cancelled = false
    void fetcher().then((res) => {
      if (!cancelled) apply(res)
    })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return { ...state, refresh: load }
}

// ── Users ───────────────────────────────────────────────────────────────
function adaptUser(r: RemoteAdminUser): AdminUser {
  return {
    id: r.id,
    email: r.email,
    handle: r.handle,
    tier: r.tier,
    status: r.status,
    createdAt: r.createdAt,
    lastLoginAt: r.lastLoginAt,
    createdAtMs: r.createdAtMs ?? null,
    lastLoginAtMs: r.lastLoginAtMs ?? null,
    deviceCount: r.deviceCount,
    shareCount: r.shareCount,
    inboxCount: r.inboxCount,
    billing: r.billing,
    emailVerified: typeof r.emailVerified === 'boolean' ? r.emailVerified : null,
    emailVerifiedAt: r.emailVerifiedAt ?? null,
    referralCode: r.referralCode ?? null,
    referredBy: r.referredBy ?? null,
    referrals: r.referrals ?? null,
    trialActive: r.trialActive,
    trialDaysLeft: r.trialDaysLeft,
    trialStartedAt: r.trialStartedAt,
    trialExpiresAt: r.trialExpiresAt,
    telemetryReportedAt: r.telemetryReportedAt ?? null,
    telemetryWidgets: r.telemetryWidgets ?? null,
    telemetryVersion: r.telemetryVersion ?? null,
    telemetryPlatform: r.telemetryPlatform ?? null,
    onboardingCompleted: r.onboardingCompleted ?? null,
    onboardingModules: r.onboardingModules ?? null
  }
}
export function useRemoteUsers() {
  return useRemoteList<RemoteAdminUser, AdminUser>(
    listUsers,
    (b) => (b.users as RemoteAdminUser[]) ?? [],
    adaptUser
  )
}

// ── Tickets ─────────────────────────────────────────────────────────────
function adaptTicket(r: RemoteTicket): AdminTicket {
  return {
    id: r.id,
    userId: r.accountId ?? 'unknown',
    userEmail: r.userEmail,
    subject: r.subject,
    body: r.body,
    status: r.status,
    priority: r.priority,
    category: r.category ?? 'General',
    createdAt: r.createdAt,
    updatedAt: r.updatedAt,
    replyCount: r.replyCount
  }
}
export function useRemoteTickets() {
  return useRemoteList<RemoteTicket, AdminTicket>(
    listTickets,
    (b) => (b.tickets as RemoteTicket[]) ?? [],
    adaptTicket
  )
}

// ── Metrics ─────────────────────────────────────────────────────────────
function adaptMetric(r: RemoteDailyMetric): DailyMetric {
  return {
    date: r.date,
    signups: r.signups,
    dau: r.dau,
    shareLinksCreated: r.shareLinksCreated,
    speeddeckPresses: r.speeddeckPresses,
    aiCalls: r.aiCalls
  }
}
/**
 * What the "active accounts" figure can see (GET /admin/metrics activityBasis).
 * Days on which an account only used the app are counted only for accounts
 * with usage reporting on, so pages must show this beside the number. Null
 * until loaded, and null when the server is too old to report it, in which
 * case pages say the basis is unknown rather than guess.
 */
export function useActivityBasis(): ActivityBasis | null {
  const [basis, setBasis] = useState<ActivityBasis | null>(null)
  useEffect(() => {
    let cancelled = false
    void listMetrics().then((res) => {
      if (!cancelled && res.ok) setBasis(res.data.activityBasis ?? null)
    })
    return () => {
      cancelled = true
    }
  }, [])
  return basis
}

/** The sentence that must accompany any active-accounts number. */
export function activityBasisNote(basis: ActivityBasis | null): string {
  const base = 'Signed in that day, or used the app with usage reporting on.'
  if (!basis) return `${base} Accounts with usage reporting off count only on days they sign in, so this is a floor.`
  return (
    `${base} Usage reporting is on for ${basis.usageReportingAccounts} of ${basis.totalAccounts} accounts; ` +
    'the rest count only on days they sign in, so this is a floor.'
  )
}

export function useRemoteMetrics() {
  return useRemoteList<RemoteDailyMetric, DailyMetric>(
    listMetrics,
    (b) => (b.days as RemoteDailyMetric[]) ?? [],
    adaptMetric
  )
}

// ── Marketplace ─────────────────────────────────────────────────────────
function adaptMarketplace(r: RemoteMarketplaceItem): MarketplaceItem {
  return {
    id: r.id,
    kind: r.kind,
    title: r.title,
    blurb: r.blurb,
    authorHandle: r.authorHandle ?? 'unknown',
    priceCents: r.priceCents,
    status: r.status,
    installCount: r.installCount,
    rating: r.rating,
    createdAt: r.createdAt,
    tags: r.tags
  }
}
export function useRemoteMarketplace() {
  return useRemoteList<RemoteMarketplaceItem, MarketplaceItem>(
    listMarketplace,
    (b) => (b.items as RemoteMarketplaceItem[]) ?? [],
    adaptMarketplace
  )
}
